import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Search, ShoppingBag, HelpCircle } from 'lucide-react';
import { RewardRedemptionItem } from '@/types';
import { Button } from '@/components/Button';
import { Copy, CheckCircle2, Eye, EyeOff } from 'lucide-react';

interface RewardsHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const STATUS_TRANSLATIONS: Record<string, string> = {
    'requested': 'Aguardando',
    'processing': 'Em Processamento',
    'fulfilled': 'Entregue',
    'rejected': 'Rejeitado',
    'canceled': 'Cancelado',
};

const STATUS_TIMELINES: Record<string, string> = {
    'requested': 'Pedido recebido. Aguardando início do processamento.',
    'processing': 'O seu pedido já está sendo processado por nossa equipe.',
    'fulfilled': 'Benefício entregue! Verifique seu email para mais detalhes.',
    'rejected': 'O pedido foi rejeitado. Seus pontos não foram descontados.',
    'canceled': 'O pedido foi cancelado ou ocorreu uma falha.',
};

const STATUS_COLORS: Record<string, string> = {
    'requested': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    'processing': 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    'fulfilled': 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    'rejected': 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    'canceled': 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
};

const PENDING_REDEMPTION_REQUESTS_STATUS = ['requested', 'processing'];

export const RewardsHistoryModal = ({ isOpen, onClose }: RewardsHistoryModalProps) => {
    const [items, setItems] = useState<RewardRedemptionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [copiedCode, setCopiedCode] = useState<string | null>(null);
    const [revealedIds, setRevealedIds] = useState<string[]>([]);

    const observerTarget = useRef<HTMLDivElement>(null);
    const initialLoadRef = useRef(false);

    const ITEMS_PER_PAGE = 15;

    const handleCopyCode = (code: string) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const toggleReveal = (id: string) => {
        setRevealedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    useEffect(() => {
        if (isOpen) {
            if (!initialLoadRef.current) {
                resetAndFetch();
                initialLoadRef.current = true;
            }
        } else {
            initialLoadRef.current = false;
        }
    }, [isOpen]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    fetchPage(page + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading, page]);

    const resetAndFetch = () => {
        setItems([]);
        setPage(0);
        setHasMore(true);
        fetchPage(0);
    };

    const fetchPage = async (pageToFetch: number) => {
        setLoading(true);
        try {
            const from = pageToFetch * ITEMS_PER_PAGE;
            const to = from + ITEMS_PER_PAGE - 1;

            const { data, error } = await supabase
                .from('points_redemption_requests')
                .select('*')
                .order('created_at', { ascending: false })
                .range(from, to);

            if (error) throw error;

            const newItems = data || [];

            if (newItems.length < ITEMS_PER_PAGE) {
                setHasMore(false);
            }

            setItems(prev => pageToFetch === 0 ? newItems : [...prev, ...newItems]);
            setPage(pageToFetch);

        } catch (err) {
            console.error('Error fetching redemptions:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-600 dark:text-purple-400">
                            <ShoppingBag className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Meus Resgates</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-0 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                    {items.length === 0 && !loading ? (
                        <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">Você ainda não realizou nenhum resgate.</p>
                            <p className="text-sm text-gray-400 mt-1">
                                Aproveite seus pontos na lojinha e eles aparecerão aqui.
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {items.map((item) => {
                                const statusLabel = STATUS_TRANSLATIONS[item.status] || item.status;
                                const statusColor = STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600';

                                const rewardTitle = item.meta?.reward?.title || 'Recompensa Indisponível';
                                const pointsCost = item.meta?.reward?.points_cost || 0;
                                const voucherCode = item.meta?.voucher_code;
                                const couponCode = item.meta?.coupon_code;
                                const expectedDeliveryDate = item.meta?.estimated_delivery_date;

                                const isRevealed = revealedIds.includes(item.id);

                                return (
                                    <div key={item.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors flex flex-col gap-2 group border-b border-gray-100 dark:border-gray-800 last:border-0">
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col gap-0.5 max-w-[60%]">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={rewardTitle}>
                                                    {rewardTitle}
                                                </p>
                                                <p className="text-xs text-gray-400 font-mono">
                                                    {formatDate(item.created_at)}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusColor}`}>
                                                        {statusLabel}
                                                    </span>
                                                    <div className="group/tooltip relative flex items-center">
                                                        <HelpCircle className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                                                        <div className="absolute right-0 top-full mt-1 w-48 p-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-xl shadow-xl opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all z-10 pointer-events-none text-right">
                                                            {STATUS_TIMELINES[item.status] || 'Status da solicitação.'}
                                                            <div className="absolute right-1 -top-1 w-2 h-2 bg-gray-900 dark:bg-white transform rotate-45"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Custo: {pointsCost.toLocaleString('pt-BR')} pts
                                                </p>
                                            </div>
                                        </div>

                                        {item.status === 'fulfilled' && (
                                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg px-3 py-2 flex items-center justify-between mt-1 border border-purple-100 dark:border-purple-800/30">
                                                <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Entrega feita em:</span>
                                                <span className="text-xs font-bold text-purple-700 dark:text-purple-300">
                                                    {item.fulfilled_at ? formatDate(item.fulfilled_at) : formatDate(item.updated_at)}
                                                </span>
                                            </div>
                                        )}

                                        {voucherCode && item.status === 'fulfilled' && (
                                            <div className="mt-2 bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 flex flex-col gap-2">
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Código de Resgate</span>
                                                <div className="flex items-center gap-2">
                                                    <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 rounded-lg text-sm font-bold text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 break-all select-all">
                                                        {isRevealed ? voucherCode : <span className="tracking-widest">••••••••••••••••</span>}
                                                    </code>
                                                    <Button
                                                        variant="secondary"
                                                        className="flex-shrink-0 !p-2 h-auto"
                                                        onClick={() => toggleReveal(item.id)}
                                                        title={isRevealed ? "Ocultar código" : "Revelar código"}
                                                    >
                                                        {isRevealed ? <EyeOff className="w-4 h-4 text-gray-500 dark:text-gray-400" /> : <Eye className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                                                    </Button>
                                                    <Button
                                                        variant="secondary"
                                                        className="flex-shrink-0 !p-2 h-auto"
                                                        onClick={() => handleCopyCode(voucherCode)}
                                                        title="Copiar código"
                                                    >
                                                        {copiedCode === voucherCode ? (
                                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {couponCode && item.status === 'fulfilled' && (
                                            <div className="mt-2 bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 flex flex-col gap-2">
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cupom</span>
                                                <div className="flex items-center gap-2">
                                                    <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 rounded-lg text-sm font-bold text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 break-all">
                                                        {couponCode}
                                                    </code>
                                                    <Button
                                                        variant="secondary"
                                                        className="flex-shrink-0 !p-2 h-auto"
                                                        onClick={() => handleCopyCode(couponCode)}
                                                        title="Copiar cupom"
                                                    >
                                                        {copiedCode === couponCode ? (
                                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {expectedDeliveryDate && PENDING_REDEMPTION_REQUESTS_STATUS.includes(item.status) && (
                                            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1.5 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30">
                                                <HelpCircle className="w-3.5 h-3.5" />
                                                Previsão de Entrega: {formatDate(expectedDeliveryDate)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {hasMore && (
                                <div ref={observerTarget} className="py-6 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
                                    {loading && <div className="w-3 h-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin"></div>}
                                    {loading ? 'Carregando mais...' : ''}
                                </div>
                            )}

                            {!hasMore && items.length > 0 && (
                                <div className="py-6 text-center text-xs text-gray-400 bg-gray-50/30 dark:bg-gray-800/20">
                                    Fim da lista
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 text-center text-[10px] text-gray-400">
                    Mostrando {items.length} resgates no total
                </div>
            </div>
        </div>
    );
};
